import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Link, NavLink, useMatches, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useProfile } from 'context/profile';
import type Profile from 'models/Profile';
import { Accessor } from 'models/Profile';
import CaretDown from 'svg/CaretDown';
import CaretRight from 'svg/CaretRight';

type SubNavLeaf = { subtitle: string; to: string; permission?: string | string[]; visible?: boolean };
type SubNavGroup = { subtitle: string; items: SubNavLeaf[]; permission?: string | string[]; visible?: boolean };
export type SubNavItem = SubNavLeaf | SubNavGroup;

function isGroup(item: SubNavItem): item is SubNavGroup {
  return 'items' in item;
}

function isRouteMatch(matchedPathnames: Set<string>, to: string) {
  return matchedPathnames.has(to);
}

function findBestMatch(items: SubNavItem[], matchedPathnames: Set<string>): string | undefined {
  return items
    .flatMap((item) => (isGroup(item) ? item.items : [item]))
    .filter((leaf) => isRouteMatch(matchedPathnames, leaf.to))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;
}

function hasPermission(profile: Profile, permission?: string | string[]) {
  if (!permission) return true;
  const perms = Array.isArray(permission) ? permission : [permission];
  return perms.some((p) => profile.has(p as Accessor));
}

const outerStyles = css`
  &.active {
    background-color: ${({ theme }) => theme.colors.blue75};
  }
`;

const colorChangeStyles = css`
  color: ${({ theme }) => theme.colors.white75};
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  text-decoration: none;
  font-size: 14px;
  line-height: 16px;
  &.active {
    color: ${({ theme }) => theme.colors.white};
    font-weight: 500;

    & svg {
      opacity: 1;
    }
  }
`;

const menuItemStyles = css`
  align-items: center;
  cursor: pointer;
  display: flex;
  padding: 14px;

  & > svg,
  & > div {
    margin-right: 10px;
    opacity: 0.5;
  }
`;

const StyledNavLink = styled(NavLink).attrs({
  draggable: false,
})`
  ${colorChangeStyles}
  ${menuItemStyles}
  ${outerStyles}
`;

const StyledSubLink = styled(Link).attrs({
  draggable: false,
})`
  ${colorChangeStyles}
  ${menuItemStyles}
  ${outerStyles}
  font-weight: normal;
`;

const ExpandedNavLink = styled.div`
  ${menuItemStyles}
`;

const StyledNavLinkContainer = styled.div`
  ${colorChangeStyles}
  display: flex;
  flex-direction: column;
`;

const FlexSpacer = styled.div`
  flex: 1;
`;

const CaretWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
`;

type Props = PropsWithChildren<{
  icon?: React.JSX.Element;
  title: string;
  to: string | SubNavItem[];
  visible?: boolean;
  end?: boolean;
  'data-cy'?: string;
}>;

function SideNavLink({ icon, title = '', to = '', visible = true, end = false, ...props }: Props) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { profile } = useProfile();
  const matches = useMatches();
  const matchedPathnames = new Set(matches.map((m) => m.pathname));

  const visibleItems = Array.isArray(to)
    ? to.filter((item) => item.visible !== false && hasPermission(profile, item.permission))
    : null;

  const isActiveInArray =
    (typeof to === 'string' && isRouteMatch(matchedPathnames, to)) ||
    (visibleItems &&
      visibleItems.some((item) =>
        isGroup(item)
          ? item.items.some((leaf) => isRouteMatch(matchedPathnames, leaf.to))
          : isRouteMatch(matchedPathnames, item.to)
      ));
  useEffect(() => {
    if (isActiveInArray && !expanded) {
      setExpanded(true);
    }
    if (!isActiveInArray && expanded) {
      setExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveInArray]);

  const handleOuterClick = () => {
    if (isActiveInArray) {
      setExpanded(!expanded);
    } else if (visibleItems?.length) {
      const first = visibleItems[0];
      const firstTo = isGroup(first) ? first.items[0]?.to : first.to;
      if (firstTo) navigate(firstTo);
    }
  };
  const handleCaretClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const bestMatchTo = visibleItems ? findBestMatch(visibleItems, matchedPathnames) : undefined;

  if (!visible || (visibleItems && visibleItems.length === 0)) return null;

  return visibleItems ? (
    <StyledNavLinkContainer className={isActiveInArray ? 'active' : ''} {...props}>
      <ExpandedNavLink onClick={handleOuterClick}>
        {icon}
        <span className='link-title'>{title}</span>
        <FlexSpacer />
        <CaretWrapper>
          {expanded ? <CaretDown onClick={handleCaretClick} /> : <CaretRight onClick={handleCaretClick} />}
        </CaretWrapper>
      </ExpandedNavLink>
      {expanded &&
        visibleItems.map((item) =>
          isGroup(item) ? (
            <SubNavGroupItem key={item.subtitle} group={item} />
          ) : (
            <StyledSubLink to={item.to} key={item.to} className={item.to === bestMatchTo ? 'active' : ''}>
              <Spacer />
              <span className='link-title'>{item.subtitle}</span>
            </StyledSubLink>
          )
        )}
    </StyledNavLinkContainer>
  ) : (
    <StyledNavLink to={to as string} end={end} {...props}>
      {icon}
      <span className='link-title'>{title}</span>
    </StyledNavLink>
  );
}

function SubNavGroupItem({ group }: { group: SubNavGroup }) {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const matches = useMatches();
  const matchedPathnames = new Set(matches.map((m) => m.pathname));

  const visibleLeaves = group.items.filter((leaf) => hasPermission(profile, leaf.permission));
  const isActive = visibleLeaves.some((leaf) => isRouteMatch(matchedPathnames, leaf.to));
  const [expanded, setExpanded] = useState(isActive);

  useEffect(() => {
    if (isActive && !expanded) setExpanded(true);
    if (!isActive && expanded) setExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  if (visibleLeaves.length === 0) return null;

  const handleClick = () => {
    if (isActive) {
      setExpanded((prev) => !prev);
    } else {
      navigate(visibleLeaves[0].to);
    }
  };
  const handleCaretClick = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <SubGroupContainer className={isActive ? 'active' : ''}>
      <SubGroupHeader onClick={handleClick}>
        <Spacer />
        <span className='link-title'>{group.subtitle}</span>
        <FlexSpacer />
        {expanded ? <CaretDown onClick={handleCaretClick} /> : <CaretRight onClick={handleCaretClick} />}
      </SubGroupHeader>
      {expanded &&
        visibleLeaves.map((leaf) => (
          <StyledDeepNavLink to={leaf.to} key={leaf.to}>
            <DeepSpacer />
            <span className='link-title'>{leaf.subtitle}</span>
          </StyledDeepNavLink>
        ))}
    </SubGroupContainer>
  );
}

export default SideNavLink;

const Spacer = styled.div`
  width: 16px;
  min-width: 16px;
  height: 16px;
  display: inline-block;
`;

const SubGroupContainer = styled.div`
  ${colorChangeStyles}
  display: flex;
  flex-direction: column;
`;

const SubGroupHeader = styled.div`
  ${menuItemStyles}
  color: inherit;
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  text-decoration: none;
  font-size: 14px;
  line-height: 16px;
`;

const StyledDeepNavLink = styled(NavLink).attrs({ draggable: false })`
  ${colorChangeStyles}
  ${menuItemStyles}
  ${outerStyles}
  font-weight: normal;
`;

const DeepSpacer = styled.div`
  width: 36px;
  height: 18px;
  display: inline-block;
`;
